import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Navigation } from '@/components/Navigation';
import { BackButton } from '@/components/BackButton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Radio } from 'lucide-react';
import { VideoPreview } from '@/components/creator/VideoPreview';
import { PollCreator } from '@/components/creator/PollCreator';
import { TipGoalsManager } from '@/components/creator/TipGoalsManager';
import { ChatModeration } from '@/components/creator/ChatModeration';
import { CohostManager } from '@/components/creator/CohostManager';
import { StreamKeyManager } from '@/components/stream/StreamKeyManager';
import { StreamQualitySettings } from '@/components/stream/StreamQualitySettings';
import { StreamHealthMonitor } from '@/components/stream/StreamHealthMonitor';
import { AdvancedModeration } from '@/components/stream/AdvancedModeration';
import { uploadRecording } from '@/utils/recordingUpload';

export default function GoLive() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [thumbnail, setThumbnail] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSponsored, setIsSponsored] = useState(false);
  const [sponsorName, setSponsorName] = useState('');
  const [isExclusive, setIsExclusive] = useState(false);
  const [saveRecording, setSaveRecording] = useState(true);
  const [streamId, setStreamId] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamKey, setStreamKey] = useState<string | null>(null);
  const [shouldStopCamera, setShouldStopCamera] = useState(false);

  // Check for existing active stream on mount
  useEffect(() => {
    const checkExistingStream = async () => {
      if (!user?.id) return;

      try {
        // Check streams_management for any active stream config (created or live)
        const { data: streamManagement } = await supabase
          .from('streams_management')
          .select('stream_key, status, title, description')
          .eq('user_id', user.id)
          .in('status', ['created', 'live'])
          .maybeSingle();

        if (streamManagement) {
          // Get corresponding stream record
          const { data: streamRecord } = await supabase
            .from('streams')
            .select('id, title, category, thumbnail_url, is_sponsored, sponsor_name, is_exclusive, status')
            .eq('creator_id', user.id)
            .in('status', ['live', 'scheduled'])
            .order('created_at', { ascending: false })
            .maybeSingle();

          if (streamRecord) {
            // Restore state from existing live/scheduled stream
            setStreamId(streamRecord.id);
            setStreamKey(streamManagement.stream_key);
            setTitle(streamRecord.title);
            setCategory(streamRecord.category || '');
            setThumbnail(streamRecord.thumbnail_url || '');
            setIsSponsored(streamRecord.is_sponsored || false);
            setSponsorName(streamRecord.sponsor_name || '');
            setIsExclusive(streamRecord.is_exclusive || false);
            setIsStreaming(streamRecord.status === 'live' || streamManagement.status === 'live');
            
            if (streamRecord.status === 'live') {
              toast.info('Reconnected to your live stream');
            } else {
              toast.info('Stream configuration restored - ready to go live');
            }
          } else {
            // No streams row yet, but configuration exists (status: created)
            setStreamId(null);
            setStreamKey(streamManagement.stream_key);
            if (!title) setTitle(streamManagement.title || title);
            setIsStreaming(false);
            toast.info('Stream configuration restored - ready to go live');
          }
        }
      } catch (error) {
        console.error('Error checking for existing stream:', error);
      }
    };

    checkExistingStream();
  }, [user?.id]);

  const handleStartStream = async () => {
    if (!title || !category) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (title.length < 3) {
      toast.error('Title must be at least 3 characters');
      return;
    }

    if (isSponsored && !sponsorName?.trim()) {
      toast.error('Sponsor name is required for sponsored streams');
      return;
    }

    setLoading(true);
    try {
      // Check if user already has a stream configuration
      const { data: existingConfig, error: existingErr } = await supabase
        .from('streams_management')
        .select('id, status, stream_key, title, description')
        .eq('user_id', user?.id)
        .in('status', ['created', 'live'])
        .maybeSingle();

      if (existingErr) throw existingErr;

      if (existingConfig?.status === 'live') {
        toast.error('You already have an active live stream. End it before starting a new one.');
        setLoading(false);
        return;
      }

      let usedStreamKey: string | null = null;
      // Reuse existing "created" config or create a new one
      if (existingConfig?.status === 'created') {
        usedStreamKey = existingConfig.stream_key;
        // Optionally update config title/description to latest
        await supabase
          .from('streams_management')
          .update({ title: title.trim(), description: `${category}${isSponsored ? ' - Sponsored by ' + sponsorName : ''}` })
          .eq('id', existingConfig.id);
      } else {
        // Create stream configuration
        const { data: streamConfig, error: createError } = await supabase.functions.invoke('create-stream', {
          body: { 
            title: title.trim(),
            description: `${category}${isSponsored ? ' - Sponsored by ' + sponsorName : ''}`
          }
        });
        if (createError) throw createError;
        if (!streamConfig?.stream) throw new Error('Failed to create stream configuration');
        usedStreamKey = streamConfig.stream.stream_key;
      }

      // Create stream record in streams table
      const { data: streamRecord, error: insertError } = await supabase
        .from('streams')
        .insert({
          creator_id: user?.id,
          title: title.trim(),
          category,
          thumbnail_url: thumbnail || null,
          status: 'live',
          is_sponsored: isSponsored,
          sponsor_name: isSponsored ? sponsorName?.trim() : null,
          is_exclusive: isExclusive,
          started_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (insertError) throw insertError;

      setStreamId(streamRecord.id);
      setStreamKey(usedStreamKey as string);
      setIsStreaming(true);
      toast.success('🎥 Stream configuration ready! Start broadcasting below.');

      // Send live notifications to followers
      try {
        await supabase.functions.invoke('notify-stream-live', {
          body: {
            streamId: streamRecord.id,
            creatorId: user?.id,
            title: title.trim(),
          }
        });
      } catch (notifyError) {
        console.error('Error sending notifications:', notifyError);
        // Don't fail the stream start if notifications fail
      }
    } catch (error: any) {
      console.error('Error starting stream:', error);
      toast.error(error.message || 'Failed to start stream. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStopStream = async () => {
    if (!streamId || !streamKey) return;

    setLoading(true);
    try {
      // Mark stream as stopped in streams_management
      const { error: stopError } = await supabase.functions.invoke('mark-stream-stopped', {
        body: { stream_key: streamKey }
      });

      if (stopError) throw stopError;

      // Update stream record
      await supabase
        .from('streams')
        .update({ 
          status: 'ended',
          ended_at: new Date().toISOString()
        })
        .eq('id', streamId);

      // Trigger camera stop
      setShouldStopCamera(true);
      
      setStreamId(null);
      setStreamKey(null);
      setIsStreaming(false);
      toast.success('Stream ended successfully');
    } catch (error: any) {
      console.error('Error stopping stream:', error);
      toast.error(error.message || 'Failed to stop stream');
    } finally {
      setLoading(false);
      // Reset stop trigger
      setTimeout(() => setShouldStopCamera(false), 100);
    }
  };

  const handleRecordingComplete = async (blob: Blob, duration: number) => {
    if (!saveRecording) {
      console.log('Recording not saved - creator opted out');
      toast.info('Stream recording discarded as per your preference');
      return;
    }

    if (streamId && user?.id) {
      try {
        toast.info('Uploading recording...');
        await uploadRecording(blob, {
          streamId,
          creatorId: user.id,
          title: `${title} - Recording`,
          description: `Recorded stream from ${new Date().toLocaleDateString()}`,
          duration,
          thumbnailUrl: thumbnail,
          publishRecording: saveRecording,
        });
        toast.success('Recording published successfully! Now available on your profile and discovery page.');
      } catch (error) {
        console.error('Failed to save recording:', error);
        toast.error('Failed to save recording');
      }
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navigation />
      
      <div className="container py-8 max-w-7xl flex-1">
        <BackButton to="/" />
        <h1 className="text-2xl font-bold mb-8">GO LIVE</h1>
        
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Setup Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Video Preview */}
            <Card>
              <CardContent className="p-6">
                <VideoPreview 
                  isStreamActive={isStreaming}
                  streamId={streamId || undefined}
                  enableBroadcast={isStreaming}
                  shouldStopCamera={shouldStopCamera}
                  onRecordingComplete={handleRecordingComplete}
                />
                <div className="mt-4 text-center">
                  {isStreaming ? (
                    <p className="text-sm text-destructive font-semibold flex items-center justify-center gap-2">
                      <span className="w-3 h-3 bg-destructive rounded-full animate-pulse"></span>
                      LIVE - Broadcasting & Recording
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Preview your camera and audio before going live
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Stream Details */}
            <Card>
              <CardContent className="p-6 space-y-4">
                <div className="space-y-2">
                  <Label>Stream Title</Label>
                  <Input
                    placeholder="Enter stream title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    maxLength={100}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select value={category} onValueChange={setCategory}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gaming">Gaming</SelectItem>
                        <SelectItem value="music">Music</SelectItem>
                        <SelectItem value="talk">Talk</SelectItem>
                        <SelectItem value="creative">Creative</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Thumbnail URL (optional)</Label>
                    <Input
                      placeholder="https://..."
                      value={thumbnail}
                      onChange={(e) => setThumbnail(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="sponsored"
                    checked={isSponsored}
                    onCheckedChange={(checked) => setIsSponsored(checked as boolean)}
                  />
                  <Label htmlFor="sponsored">Sponsored Event</Label>
                </div>

                {isSponsored && (
                  <div className="space-y-2">
                    <Label htmlFor="sponsor">Sponsor Name</Label>
                    <Input
                      id="sponsor"
                      value={sponsorName}
                      onChange={(e) => setSponsorName(e.target.value)}
                      placeholder="e.g., Universal Music"
                    />
                  </div>
                )}

                <div className="flex items-center space-x-2 pt-2 border-t">
                  <Checkbox
                    id="saveRecording"
                    checked={saveRecording}
                    onCheckedChange={(checked) => setSaveRecording(checked as boolean)}
                  />
                  <Label htmlFor="saveRecording" className="cursor-pointer">
                    Save and publish recording after stream ends
                  </Label>
                </div>
                {saveRecording && (
                  <p className="text-sm text-muted-foreground pl-6">
                    Recording will be automatically published to your profile and discovery page
                  </p>
                )}

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="exclusive"
                    checked={isExclusive}
                    onCheckedChange={(checked) => setIsExclusive(checked as boolean)}
                  />
                  <Label htmlFor="exclusive">Exclusive to Subscribers</Label>
                </div>

                {!isStreaming ? (
                  <Button 
                    className="w-full" 
                    size="lg"
                    onClick={handleStartStream}
                    disabled={loading}
                  >
                    <Radio className="mr-2 h-5 w-5" />
                    {loading ? 'Starting Stream...' : 'START STREAM'}
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <Button 
                      className="w-full" 
                      size="lg"
                      variant="destructive"
                      onClick={handleStopStream}
                      disabled={loading}
                    >
                      {loading ? 'Stopping...' : 'END STREAM'}
                    </Button>
                    <Button 
                      className="w-full" 
                      variant="outline"
                      onClick={() => navigate(`/stream/${streamId}`)}
                    >
                      View Stream Page
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Poll Creator */}
            <PollCreator />

            {/* Cohost Manager */}
            {streamId && <CohostManager streamId={streamId} isCreator={true} />}
          </div>

          {/* Tools Sidebar */}
          <div className="space-y-6">
            {/* Stream Configuration */}
            <StreamKeyManager streamId={streamId || undefined} />

            {/* Stream Quality */}
            <StreamQualitySettings />

            {/* Stream Health */}
            <StreamHealthMonitor 
              streamId={streamId || undefined} 
              isLive={streamId !== null}
              viewerCount={0}
            />

            {/* Advanced Moderation */}
            <AdvancedModeration streamId={streamId || undefined} />

            {/* Tip Goals */}
            <TipGoalsManager />

            {/* Basic Chat Moderation */}
            <ChatModeration />
          </div>
        </div>
      </div>
    </div>
  );
}
